import { QueryClient } from '@tanstack/react-query';

/**
 * TanStack Query Client Configuration
 *
 * Centralized configuration for React Query with optimized caching strategies
 * for the task management application.
 *
 * Cache Strategy:
 * - Projects: 5 minutes stale time (changes infrequently)
 * - Tasks: 10 seconds stale time (changes frequently via real-time updates)
 * - Garbage collection: 10 minutes (keeps unused data in cache)
 *
 * Auto-refetch behavior:
 * - On window focus: Keep data fresh when user returns to tab
 * - On reconnect: Sync data after network restoration
 * - On mount: Fetch fresh data on component mount
 *
 * Retry strategy:
 * - 3 retries with exponential backoff for resilient API communication
 */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Stale time: How long cached data is considered fresh
      // Default is 0 (immediately stale), we set sensible defaults
      staleTime: 10 * 1000, // 10 seconds for tasks (default)

      // Garbage collection time: How long unused data stays in cache
      gcTime: 10 * 60 * 1000, // 10 minutes

      // Auto-refetch on window focus (keep data fresh when user returns)
      refetchOnWindowFocus: true,

      // Auto-refetch on network reconnection
      refetchOnReconnect: true,

      // Auto-refetch on mount (get fresh data when component mounts)
      refetchOnMount: true,

      // Retry failed requests 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

      // Network mode: online (fail if offline)
      networkMode: 'online',
    },
    mutations: {
      // Retry mutations once (be conservative with mutations)
      retry: 1,
      retryDelay: 1000,

      // Network mode: online (fail if offline)
      networkMode: 'online',
    },
  },
});

/**
 * Query Key Factory
 *
 * Centralized query key definitions for consistent caching and invalidation.
 * Follow TanStack Query conventions: array-based, hierarchical keys.
 *
 * Pattern: ['domain', 'resource', ...filters]
 *
 * Examples:
 * - ['users', userId] -> GET /v1/users/:userId
 * - ['projects'] -> GET /v1/projects
 * - ['projects', projectId, 'tasks'] -> GET /v1/tasks?project_id=:projectId
 */
export const queryKeys = {
  // User queries
  user: (userId: string) => ['users', userId] as const,

  // Project queries
  projects: () => ['projects'] as const,
  project: (projectId: string) => ['projects', projectId] as const,

  // Task queries
  tasks: (projectId: string) => ['projects', projectId, 'tasks'] as const,
  task: (projectId: string, taskId: string) => ['projects', projectId, 'tasks', taskId] as const,
} as const;

/**
 * Custom Query Options
 *
 * Preset configurations for common query patterns with optimized stale times.
 */
export const queryOptions = {
  // Projects change infrequently - cache for 5 minutes
  projects: {
    staleTime: 5 * 60 * 1000, // 5 minutes
  },

  // Tasks change frequently - cache for 10 seconds
  tasks: {
    staleTime: 10 * 1000, // 10 seconds
  },

  // User data rarely changes - cache for 1 hour
  user: {
    staleTime: 60 * 60 * 1000, // 1 hour
  },
} as const;
